import { Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";

type MicroOption = {
  key: "start" | "tools" | "time" | "stopped";
  label: string;
  followUp: string;
};

export type LandingAddOnCopy = {
  micro: {
    question: string;
    helper: string;
    primaryTag: string;
    startPointLabel: string;
    options: MicroOption[];
  };
  pause: {
    eyebrow: string;
    statement: string;
    subline: string;
  };
  courses: {
    primaryCardTag: string;
    primaryButton: string;
  };
};

export const landingAddOnCopyByLocale = defineLocaleRecord<LandingAddOnCopy>({
  en: {
    micro: {
      question: "What are you stuck on right now?",
      helper: "Pick one. We will show your starting point.",
      primaryTag: "Most common",
      startPointLabel: "Your starting point is set.",
      options: [
        { key: "start", label: "I don’t know where to start", followUp: "Okay. We start with one clear first step." },
        { key: "tools", label: "Too many tools", followUp: "Makes sense. We narrow it down." },
        { key: "time", label: "No time to figure things out", followUp: "Understood. We keep it short and practical." },
        { key: "stopped", label: "I tried before and stopped", followUp: "Got it. We remove the friction first." },
      ],
    },
    pause: {
      eyebrow: "Pause",
      statement: "You don’t need to try harder.",
      subline: "You need one clear next step.",
    },
    courses: {
      primaryCardTag: "Start here",
      primaryButton: "Start with this",
    },
  },
  fr: {
    micro: {
      question: "Ou etes-vous bloque en ce moment ?",
      helper: "Choisissez une option. Nous montrons votre point de depart.",
      primaryTag: "Le plus frequent",
      startPointLabel: "Votre point de depart est defini.",
      options: [
        { key: "start", label: "Je ne sais pas par ou commencer", followUp: "D'accord. On commence par une premiere etape claire." },
        { key: "tools", label: "Trop d'outils", followUp: "Compris. On reduit les choix." },
        { key: "time", label: "Je n'ai pas le temps de tout comprendre", followUp: "Compris. On garde cela court et concret." },
        { key: "stopped", label: "J'ai essaye puis j'ai arrete", followUp: "Compris. On retire d'abord le blocage." },
      ],
    },
    pause: {
      eyebrow: "Pause",
      statement: "Vous n'avez pas besoin d'essayer plus fort.",
      subline: "Vous avez besoin d'une prochaine etape claire.",
    },
    courses: {
      primaryCardTag: "Commencez ici",
      primaryButton: "Commencer par ceci",
    },
  },
  ru: {
    micro: {
      question: "Где вы застряли прямо сейчас?",
      helper: "Выберите один вариант. Мы покажем вашу точку старта.",
      primaryTag: "Чаще всего",
      startPointLabel: "Ваша стартовая точка определена.",
      options: [
        { key: "start", label: "Не понимаю, с чего начать", followUp: "Хорошо. Начнем с одного понятного шага." },
        { key: "tools", label: "Слишком много инструментов", followUp: "Понятно. Сузим выбор." },
        { key: "time", label: "Нет времени разбираться", followUp: "Понял. Будет коротко и практично." },
        { key: "stopped", label: "Пробовал и остановился", followUp: "Понял. Сначала уберем трение." },
      ],
    },
    pause: {
      eyebrow: "Пауза",
      statement: "Вам не нужно стараться сильнее.",
      subline: "Вам нужен один ясный следующий шаг.",
    },
    courses: {
      primaryCardTag: "Начните здесь",
      primaryButton: "Начать с этого",
    },
  },
  uk: {
    micro: {
      question: "Де ви застрягли прямо зараз?",
      helper: "Оберіть один варіант. Ми покажемо вашу точку старту.",
      primaryTag: "Найчастіше",
      startPointLabel: "Вашу стартову точку визначено.",
      options: [
        { key: "start", label: "Не розумію, з чого почати", followUp: "Добре. Почнемо з одного чіткого кроку." },
        { key: "tools", label: "Занадто багато інструментів", followUp: "Зрозуміло. Звузимо вибір." },
        { key: "time", label: "Немає часу розбиратися", followUp: "Зрозуміло. Буде коротко і практично." },
        { key: "stopped", label: "Пробував і зупинився", followUp: "Зрозуміло. Спершу приберемо тертя." },
      ],
    },
    pause: {
      eyebrow: "Пауза",
      statement: "Вам не потрібно старатися сильніше.",
      subline: "Вам потрібен один ясний наступний крок.",
    },
    courses: {
      primaryCardTag: "Почніть тут",
      primaryButton: "Почати з цього",
    },
  },
  de: {
    micro: {
      question: "Wo hängst du gerade fest?",
      helper: "Wähle eine Option. Wir markieren deinen Startpunkt.",
      primaryTag: "Am häufigsten",
      startPointLabel: "Dein Startpunkt ist gesetzt.",
      options: [
        { key: "start", label: "Ich weiß nicht, wo ich anfangen soll", followUp: "Okay. Wir starten mit einem klaren ersten Schritt." },
        { key: "tools", label: "Zu viele Tools", followUp: "Verstanden. Wir grenzen es ein." },
        { key: "time", label: "Keine Zeit zum Ausprobieren", followUp: "Verstanden. Wir halten es kurz und praktisch." },
        { key: "stopped", label: "Ich habe es versucht und aufgehört", followUp: "Alles klar. Wir entfernen zuerst die Reibung." },
      ],
    },
    pause: {
      eyebrow: "Pause",
      statement: "Du musst dich nicht mehr anstrengen.",
      subline: "Du brauchst einen klaren nächsten Schritt.",
    },
    courses: {
      primaryCardTag: "Starte hier",
      primaryButton: "Damit anfangen",
    },
  },
  es: {
    micro: {
      question: "¿Con que estas atascado ahora?",
      helper: "Elige una opcion. Mostramos tu punto de partida.",
      primaryTag: "Mas comun",
      startPointLabel: "Tu punto de partida esta definido.",
      options: [
        { key: "start", label: "No se por donde empezar", followUp: "Vale. Empezamos con un primer paso claro." },
        { key: "tools", label: "Demasiadas herramientas", followUp: "Entendido. Reducimos opciones." },
        { key: "time", label: "No tengo tiempo para entender todo", followUp: "Entendido. Lo mantenemos corto y practico." },
        { key: "stopped", label: "Lo intente y lo deje", followUp: "Entendido. Quitamos primero esa friccion." },
      ],
    },
    pause: {
      eyebrow: "Pausa",
      statement: "No necesitas esforzarte mas.",
      subline: "Necesitas un siguiente paso claro.",
    },
    courses: {
      primaryCardTag: "Empieza aqui",
      primaryButton: "Empezar con esto",
    },
  },
  it: {
    micro: {
      question: "Dove sei bloccato adesso?",
      helper: "Scegli un'opzione. Mostriamo il tuo punto di partenza.",
      primaryTag: "Piu comune",
      startPointLabel: "Il tuo punto di partenza e impostato.",
      options: [
        { key: "start", label: "Non so da dove iniziare", followUp: "Va bene. Partiamo con un primo passo chiaro." },
        { key: "tools", label: "Troppi strumenti", followUp: "Capito. Riduciamo le scelte." },
        { key: "time", label: "Non ho tempo per capire tutto", followUp: "Capito. Restiamo brevi e pratici." },
        { key: "stopped", label: "Ho provato e mi sono fermato", followUp: "Capito. Prima togliamo quell'attrito." },
      ],
    },
    pause: {
      eyebrow: "Pausa",
      statement: "Non devi impegnarti di più.",
      subline: "Ti serve un prossimo passo chiaro.",
    },
    courses: {
      primaryCardTag: "Inizia qui",
      primaryButton: "Inizia da qui",
    },
  },
  pt: {
    micro: {
      question: "No que você está travado agora?",
      helper: "Escolha uma opção. Vamos mostrar seu ponto de partida.",
      primaryTag: "Mais comum",
      startPointLabel: "Seu ponto de partida está definido.",
      options: [
        { key: "start", label: "Não sei por onde começar", followUp: "Tudo bem. Começamos com um primeiro passo claro." },
        { key: "tools", label: "Ferramentas demais", followUp: "Entendido. Vamos reduzir as opções." },
        { key: "time", label: "Não tenho tempo para entender tudo", followUp: "Entendido. Vamos manter curto e prático." },
        { key: "stopped", label: "Tentei antes e parei", followUp: "Entendido. Primeiro removemos o que trava." },
      ],
    },
    pause: {
      eyebrow: "Pausa",
      statement: "Você não precisa se esforçar mais.",
      subline: "Você precisa de um próximo passo claro.",
    },
    courses: {
      primaryCardTag: "Comece aqui",
      primaryButton: "Começar com isso",
    },
  },
});

export function getLandingAddOnCopy(lang: Locale): LandingAddOnCopy {
  return landingAddOnCopyByLocale[lang];
}
